/**
 * Service Worker Manager for static asset caching
 *
 * Manages service worker registration and updates.
 * vh-web is online-only - service worker only caches static assets.
 * No offline queue, background sync, or API request interception.
 *
 * Last Updated: 2026-01-31
 * - Removed offline queue and sync methods
 * - Service worker disabled in dev by default
 * - API requests bypass service worker entirely
 *
 * References:
 * - src/services/socketService.js - Service pattern
 * - public/service-worker.js - Service worker implementation
 */

import logger from '../utils/logger';

class ServiceWorkerManager {
  constructor() {
    this.registration = null;
    this.updateAvailable = false;
    this.updateCallbacks = new Set();
    this.controllerChangeCallbacks = new Set();
    this.disableSw = this.shouldDisableServiceWorker();

    // Set up message listeners for sync events
    if (navigator.serviceWorker) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data && (event.data.type === 'SYNC_COMPLETE' || event.data.type === 'SYNC_FAILED')) {
          // Events handled by onSyncComplete/onSyncFailed callbacks
        }
      });
    }

    // Set up online/offline listeners
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => {
        logger.info('[ServiceWorkerManager] Network connection restored');
      });
      window.addEventListener('offline', () => {
        logger.info('[ServiceWorkerManager] Network connection lost');
      });
    }
  }

  /**
   * Check if service worker should be disabled
   * @returns {boolean} True if SW should be disabled
   * 
   * Policy:
   * - Disabled in dev by default (VITE_DISABLE_SW not needed in dev)
   * - Can be explicitly disabled in prod with VITE_DISABLE_SW=true
   * - When enabled, SW only caches static assets (no API interception)
   */
  shouldDisableServiceWorker() {
    // Always disabled in dev
    if (import.meta.env.DEV) {
      return true;
    }
    // Can be disabled in prod via env var
    return import.meta.env.VITE_DISABLE_SW === 'true';
  }

  /**
   * Register the service worker
   * @returns {Promise<ServiceWorkerRegistration|null>} Service worker registration
   */
  async registerServiceWorker() {
    if (this.disableSw) {
      await this.unregisterAllServiceWorkers();
      logger.warn('[ServiceWorkerManager] Service worker disabled by config');
      return null;
    }

    if (!('serviceWorker' in navigator)) {
      logger.warn('[ServiceWorkerManager] Service workers not supported');
      return null;
    }

    try {
      this.registration = await navigator.serviceWorker.register('/service-worker.js');
      logger.info('[ServiceWorkerManager] Service worker registered successfully');

      // Listen for updates
      this.registration.addEventListener('updatefound', () => {
        const newWorker = this.registration.installing;
        if (newWorker) {
          this.handleUpdateFound(newWorker);
        }
      });

      // Listen for controller change (new SW activated)
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        this.handleControllerChange();
      });

      // Check for updates periodically
      setInterval(() => {
        this.checkForUpdates();
      }, 60 * 60 * 1000); // Check every hour

      return this.registration;
    } catch (error) {
      logger.error('[ServiceWorkerManager] Failed to register service worker:', error);
      return null;
    }
  }

  /**
   * Handle when a new service worker is found
   * @param {ServiceWorker} newWorker - New service worker instance
   */
  handleUpdateFound(newWorker) {
    newWorker.addEventListener('statechange', () => {
      if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
        // New version available
        this.updateAvailable = true;
        logger.info('[ServiceWorkerManager] New service worker version available');

        // Notify listeners
        for (const callback of this.updateCallbacks) {
          try {
            callback(newWorker);
          } catch (error) {
            logger.error('[ServiceWorkerManager] Update callback error:', error);
          }
        }
      }
    });
  }

  /**
   * Handle when the service worker controller changes
   */
  handleControllerChange() {
    logger.info('[ServiceWorkerManager] Service worker controller changed - new version activated');

    // Notify listeners
    for (const callback of this.controllerChangeCallbacks) {
      try {
        callback();
      } catch (error) {
        logger.error('[ServiceWorkerManager] Controller change callback error:', error);
      }
    }
  }

  /**
   * Check for service worker updates
   * @returns {Promise<void>}
   */
  async checkForUpdates() {
    if (!this.registration) return;

    try {
      await this.registration.update();
      logger.debug('[ServiceWorkerManager] Checked for service worker updates');
    } catch (error) {
      logger.error('[ServiceWorkerManager] Failed to check for updates:', error);
    }
  }

  /**
   * Force the waiting service worker to activate immediately
   * @returns {Promise<void>}
   */
  async skipWaiting() {
    if (!this.registration || !this.registration.waiting) return;

    // Send message to service worker to skip waiting
    this.registration.waiting.postMessage({ type: 'SKIP_WAITING' });
  }

  /**
   * Unregister the service worker
   * @returns {Promise<void>}
   */
  async unregisterServiceWorker() {
    if (!this.registration) return;

    try {
      const result = await this.registration.unregister();
      if (result) {
        logger.info('[ServiceWorkerManager] Service worker unregistered');
        this.registration = null;
        this.updateAvailable = false;
      }
    } catch (error) {
      logger.error('[ServiceWorkerManager] Failed to unregister service worker:', error);
    }
  }

  /**
   * Unregister all existing service workers
   * @returns {Promise<void>}
   */
  async unregisterAllServiceWorkers() {
    if (!('serviceWorker' in navigator)) return;

    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      if (registrations.length === 0) return;
      await Promise.all(registrations.map((registration) => registration.unregister()));
      this.registration = null;
      this.updateAvailable = false;
      logger.info('[ServiceWorkerManager] Unregistered all service workers', {
        count: registrations.length,
      });
    } catch (error) {
      logger.error('[ServiceWorkerManager] Failed to unregister all service workers:', error);
    }
  }

  /**
   * Get current service worker status
   * @returns {Object} Status information
   */
  getServiceWorkerStatus() {
    return {
      registered: !!this.registration,
      updateAvailable: this.updateAvailable,
      state: this.registration?.active?.state || 'unknown',
      controller: !!navigator.serviceWorker.controller,
    };
  }

  /**
   * Send a message to the active service worker
   * @param {Object} message - Message to send
   * @returns {Promise<any>} Response from service worker
   */
  async postMessageToServiceWorker(message) {
    return new Promise((resolve, reject) => {
      if (!navigator.serviceWorker.controller) {
        reject(new Error('No active service worker controller'));
        return;
      }

      const messageChannel = new MessageChannel();

      messageChannel.port1.onmessage = (event) => {
        resolve(event.data);
      };

      navigator.serviceWorker.controller.postMessage(message, [messageChannel.port2]);
    });
  }

  /**
   * Register a callback for when updates are available
   * @param {Function} callback - Callback function
   */
  onUpdateAvailable(callback) {
    this.updateCallbacks.add(callback);
  }

  /**
   * Unregister an update callback
   * @param {Function} callback - Callback function
   */
  offUpdateAvailable(callback) {
    this.updateCallbacks.delete(callback);
  }

  /**
   * Register a callback for when controller changes
   * @param {Function} callback - Callback function
   */
  onControllerChange(callback) {
    this.controllerChangeCallbacks.add(callback);
  }

  /**
   * Unregister a controller change callback
   * @param {Function} callback - Callback function
   */
  offControllerChange(callback) {
    this.controllerChangeCallbacks.delete(callback);
  }

  /**
   * Trigger a page reload to activate new service worker
   */
  reloadPage() {
    window.location.reload();
  }

  // @VH_DELETE_CANDIDATE: Offline sync methods removed - vh-web is online-only | delete_after=2026-02-15
  // Methods removed:
  // - triggerSync()
  // - triggerSubmissionSync()
  // - triggerWorkflowSync()
  // - getQueueStatus()
  // - clearQueue()
  // - onSyncComplete()
  // - onSyncFailed()
  // 
  // vh-web is online-only. Offline queues belong in vh-mobile only.

  /**
   * Check if currently offline
   * @returns {boolean} True if offline
   */
  isOffline() {
    return !navigator.onLine;
  }

  /**
   * Register callback for offline event
   * @param {Function} callback - Callback function
   */
  onOffline(callback) {
    window.addEventListener('offline', callback);
  }

  /**
   * Register callback for online event
   * @param {Function} callback - Callback function
   */
  onOnline(callback) {
    window.addEventListener('online', callback);
  }
}

// Export singleton instance
const serviceWorkerManager = new ServiceWorkerManager();
export default serviceWorkerManager;
